import type { Event } from 'react-big-calendar';

import { CALENDAR_SERIES_SCOPE } from 'app/typings/core/extranet/inventory/enums/CalendarSeriesScope';
import type { CalendarSeriesScope } from 'app/typings/core/extranet/inventory/enums/CalendarSeriesScope';
import { FREQUENCY_MODE } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';

import type { AvailabilityFormValues } from '../_components/AvailabilityForm/availabilityForm.types';
import type { TimeslotEventResource } from '../_components/CalendarEvent/CalendarEvent';

import { getEventKey } from './events';

export const EDIT_SCOPES: CalendarSeriesScope[] = [
  CALENDAR_SERIES_SCOPE.THIS,
  CALENDAR_SERIES_SCOPE.THIS_AND_NEXT,
  CALENDAR_SERIES_SCOPE.ALL,
];

export const REPEAT_SCOPES: CalendarSeriesScope[] = [
  CALENDAR_SERIES_SCOPE.THIS_AND_NEXT,
  CALENDAR_SERIES_SCOPE.ALL,
];

export const getResource = (
  event: Event
): Partial<TimeslotEventResource> | undefined =>
  event.resource as Partial<TimeslotEventResource> | undefined;

export const isRecurrentEvent = (event: Event): boolean =>
  getResource(event)?.isRecurrent === true;

const sameDays = (a: number[] = [], b: number[] = []): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const sortedB = [...b].sort((x, y) => x - y);

  return [...a]
    .sort((x, y) => x - y)
    .every((day, index) => day === sortedB[index]);
};

export const isRepeatChanged = (
  event: Event,
  values: AvailabilityFormValues
): boolean => {
  const resource = getResource(event);
  const originalFrequency =
    resource?.frequency ??
    (resource?.isRecurrent ? FREQUENCY_MODE.WEEKLY : FREQUENCY_MODE.NONE);

  if (values.frequency !== originalFrequency) {
    return true;
  }

  if (values.frequency === FREQUENCY_MODE.CUSTOM) {
    return !sameDays(values.weekdays, resource?.weekdays);
  }

  return false;
};

export const removeForScope = <T extends Event>(
  events: T[],
  event: Event,
  scope?: CalendarSeriesScope
): T[] => {
  const eventId = getEventKey(event);
  const seriesId = getResource(event)?.seriesId;
  const eventStart = event.start?.getTime() ?? 0;

  return events.filter((existing) => {
    if (!seriesId || !scope || scope === CALENDAR_SERIES_SCOPE.THIS) {
      return getEventKey(existing) !== eventId;
    }

    if (getResource(existing)?.seriesId !== seriesId) {
      return true;
    }

    if (scope === CALENDAR_SERIES_SCOPE.THIS_AND_NEXT) {
      return (existing.start?.getTime() ?? 0) < eventStart;
    }

    return false;
  });
};
